import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FiSearch, FiFilter, FiAward } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import MatchCard, { getSideLabel, formatQuota } from '../components/MatchCard/MatchCard';
import Modal from '../components/Modal/Modal';
import Pagination from '../components/Pagination/Pagination';
import WinningMarketsModal from '../components/WinningMarketsModal/WinningMarketsModal';
// Shared market-section constants — see /constants/quotaSections.js. Identical
// configuration is used by Integrations.js so both screens render the same markets.
import {
  QUOTA_CONFIG_MAP,
  QUOTA_SECTIONS,
  TT_FIELDS,
  STAT_TT_FIELDS,
  buildDynamicOuSections,
} from '../constants/quotaSections';
import p from './Pages.module.css';

const STATUSES = [{ id: 1, code: 'pending' },{ id: 2, code: 'started' },{ id: 3, code: 'finished' },{ id: 4, code: 'draft' },{ id: 5, code: 'first_half_done' }];
const TZ_OPTIONS = ['-12:00','-11:00','-10:00','-09:00','-08:00','-07:00','-06:00','-05:00','-04:00','-03:00','-02:00','-01:00','+00:00','+01:00','+02:00','+03:00','+04:00','+05:00','+05:30','+06:00','+07:00','+08:00','+09:00','+10:00','+11:00','+12:00'];

// Dynamic O/U line configs per stat category — mirrors CreateMatch.js so edit UI has parity with create UI.
const genLines = (min, max, step = 0.5) => {
  const arr = [];
  for (let v = min; v <= max; v += step) arr.push(parseFloat(v.toFixed(1)));
  return arr;
};
const STAT_OU_CONFIG = {
  corners:   { label: 'Corners FT',         prefix: 'cornersOu',   lineOpts: genLines(7.5, 13.5), max: 2 },
  htCorners: { label: 'Corners 1H',         prefix: 'htCornersOu', lineOpts: genLines(3.5, 7.5),  max: 2 },
  yc:        { label: 'Yellow Cards FT',    prefix: 'ycOu',        lineOpts: genLines(2.5, 6.5),  max: 2 },
  htYc:      { label: 'Yellow Cards 1H',    prefix: 'htYcOu',      lineOpts: genLines(0.5, 3.5),  max: 2 },
  sot:       { label: 'Shots on Target FT', prefix: 'sotOu',       lineOpts: genLines(7.5, 13.5), max: 2 },
  htSot:     { label: 'Shots on Target 1H', prefix: 'htSotOu',     lineOpts: genLines(3.5, 7.5),  max: 2 },
};
// 1X2 trio per stat category (rendered inside each category's section in the match-edit modal).
const STAT_CATEGORIES_1X2 = [
  { cat: 'corners',   keys: ['cornersR1', 'cornersRx', 'cornersR2'] },
  { cat: 'htCorners', keys: ['htCornersR1', 'htCornersRx', 'htCornersR2'] },
  { cat: 'yc',        keys: ['ycR1', 'ycRx', 'ycR2'] },
  { cat: 'htYc',      keys: ['htYcR1', 'htYcRx', 'htYcR2'] },
  { cat: 'sot',       keys: ['sotR1', 'sotRx', 'sotR2'] },
  { cat: 'htSot',     keys: ['htSotR1', 'htSotRx', 'htSotR2'] },
];
const EMPTY_M_OU_LINES = () => ({ corners: [], htCorners: [], yc: [], htYc: [], sot: [], htSot: [] });

// Real-match quota keys actually accepted by backend (REAL_QUOTA_DTO_TO_COL, 69 keys).
// Keys outside this set must be stripped from PUT /v1/match payload to avoid 422 "Unknown field".
const ALLOWED_REAL_QUOTA_KEYS = new Set([
  'rh','rd','ra','ou25o','ou25u',
  'rhth','rhtd','rhta','htou15o','htou15u',
  'shR1','shRx','shR2','shOu15o','shOu15u',
  'cornersR1','cornersRx','cornersR2',
  'cornersOuLine1','cornersOu1o','cornersOu1u',
  'cornersOuLine2','cornersOu2o','cornersOu2u',
  'htCornersR1','htCornersRx','htCornersR2',
  'htCornersOuLine1','htCornersOu1o','htCornersOu1u',
  'htCornersOuLine2','htCornersOu2o','htCornersOu2u',
  'ycR1','ycRx','ycR2',
  'ycOuLine1','ycOu1o','ycOu1u',
  'ycOuLine2','ycOu2o','ycOu2u',
  'htYcR1','htYcRx','htYcR2',
  'htYcOuLine1','htYcOu1o','htYcOu1u',
  'htYcOuLine2','htYcOu2o','htYcOu2u',
  'sotR1','sotRx','sotR2',
  'sotOuLine1','sotOu1o','sotOu1u',
  'sotOuLine2','sotOu2o','sotOu2u',
  'htSotR1','htSotRx','htSotR2',
  'htSotOuLine1','htSotOu1o','htSotOu1u',
  'htSotOuLine2','htSotOu2o','htSotOu2u',
]);

// Required-on-create per FRONTEND_MARKET_OPT_IN.md — always sent on update too (cannot be null).
const REQUIRED_REAL_QUOTA_KEYS = new Set(['rh','rd','ra','ou25o','ou25u','rhth','rhtd','rhta','htou15o','htou15u']);
const REQUIRED_TEAM_TT_KEYS = new Set(['ttft15o','ttft15u','ttht05o','ttht05u','ttsh05o','ttsh05u']);

// Time of first goal helpers: seconds ↔ mm:ss.SSS
function secsToTimeStr(secs) {
  if (secs == null || secs === '') return '';
  // If already a mm:ss or mm:ss.SSS string, return as-is
  if (typeof secs === 'string' && /^\d+:\d{1,2}(\.\d+)?$/.test(secs)) return secs;
  const s = Number(secs);
  if (isNaN(s)) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const wholeSec = Math.floor(sec);
  const ms = Math.round((sec - wholeSec) * 1000);
  return `${String(m).padStart(2, '0')}:${String(wholeSec).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}
function timeStrToSecs(str) {
  if (!str || str === '') return null;
  return str; // backend accepts mm:ss.SSS string format directly
}
// Parse timeOfFirstGoal to comparable number (seconds): handles string "mm:ss.SSS" or number
function parseGoalTime(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  const m = String(v).match(/^(\d+):(\d{1,2})(?:\.(\d{1,3}))?$/);
  if (m) return parseInt(m[1],10)*60 + parseInt(m[2],10) + (m[3] ? parseInt(m[3].padEnd(3,'0'),10)/1000 : 0);
  const n = Number(v); return isNaN(n) ? null : n;
}

// All team-level field keys for formatting/serializing
const ALL_TEAM_KEYS = [
  'ttft15o', 'ttft15u', 'ttht05o', 'ttht05u', 'ttsh05o', 'ttsh05u',
  'cornersTtLine', 'cornersTtO', 'cornersTtU', 'htCornersTtLine', 'htCornersTtO', 'htCornersTtU',
  'ycTtLine', 'ycTtO', 'ycTtU', 'htYcTtLine', 'htYcTtO', 'htYcTtU',
  'sotTtLine', 'sotTtO', 'sotTtU', 'htSotTtLine', 'htSotTtO', 'htSotTtU',
];

// Extra per-team match-result stat fields (additive — backend nullable).
// Used in the result-entry modals (real match + clasico inline editor).
const EXTRA_RESULT_FIELDS = [
  { key: 'secondHalfGoals', label: '2H Goals' },
  { key: 'htCorners', label: 'HT Corners' },
  { key: 'corners', label: 'FT Corners' },
  { key: 'htYellowCards', label: 'HT Yellow' },
  { key: 'yellowCards', label: 'FT Yellow' },
  { key: 'redCards', label: 'Red Cards' },
  { key: 'htShotsOnTarget', label: 'HT SoT' },
  { key: 'shotsOnTarget', label: 'FT SoT' },
  { key: 'fouls', label: 'Fouls' },
  { key: 'offsides', label: 'Offsides' },
  { key: 'possessionPct', label: 'Possession %', float: true },
];
const EXTRA_RESULT_KEYS = EXTRA_RESULT_FIELDS.map(f => f.key);
const pickExtras = (src, fallback = '') => {
  const out = {};
  for (const k of EXTRA_RESULT_KEYS) out[k] = src?.[k] ?? fallback;
  return out;
};

const MATCH_QUOTA_SECTIONS = [
  { label: 'FT — 1x2', fields: [{ key: 'rh', l: 'Home' },{ key: 'rd', l: 'Draw' },{ key: 'ra', l: 'Away' }] },
  { label: 'FT — O/U 2.5', fields: [{ key: 'ou25o', l: 'Over' },{ key: 'ou25u', l: 'Under' }] },
  { label: 'HT — 1x2', fields: [{ key: 'rhth', l: 'Home' },{ key: 'rhtd', l: 'Draw' },{ key: 'rhta', l: 'Away' }] },
  { label: 'HT — O/U 1.5', fields: [{ key: 'htou15o', l: 'Over' },{ key: 'htou15u', l: 'Under' }] },
  { label: '2H — 1x2', fields: [{ key: 'shR1', l: 'Home' },{ key: 'shRx', l: 'Draw' },{ key: 'shR2', l: 'Away' }] },
  { label: '2H — O/U 1.5', fields: [{ key: 'shOu15o', l: 'Over' },{ key: 'shOu15u', l: 'Under' }] },
];

// Inline editor for dynamic O/U lines on the match-edit modal (mirrors CreateMatch.js DynamicOuLines).
function MatchOuLinesEditor({ catKey, config, lines, setLines }) {
  const [selected, setSelected] = useState('');
  const usedLines = (lines || []).map(l => l.line);
  const available = config.lineOpts.filter(v => !usedLines.includes(v));
  const canAdd = (lines || []).length < config.max && available.length > 0;
  const updateCat = (mutator) => setLines(prev => ({ ...prev, [catKey]: mutator(prev[catKey] || []) }));
  const onAdd = () => {
    if (!selected) return;
    updateCat(arr => [...arr, { line: parseFloat(selected), over: '', under: '' }]);
    setSelected('');
  };
  const onRemove = (idx) => updateCat(arr => arr.filter((_, i) => i !== idx));
  const onChange = (idx, field, value) => updateCat(arr => arr.map((entry, i) => i === idx ? { ...entry, [field]: value } : entry));

  return (
    <div style={{ marginTop: 6 }}>
      {canAdd && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <select className={p.formSelect} style={{ width: 130 }} value={selected} onChange={e => setSelected(e.target.value)}>
            <option value="">Select line…</option>
            {available.map(v => <option key={v} value={v}>O/U {v}</option>)}
          </select>
          <button className={p.btnOutline} style={{ fontSize: '0.75rem', padding: '5px 10px' }} disabled={!selected} onClick={onAdd}>+ Add Line</button>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{(lines || []).length}/{config.max}</span>
        </div>
      )}
      {(lines || []).map((entry, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 6, padding: '6px 10px', background: 'var(--bg-primary)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--accent)', minWidth: 60 }}>O/U {entry.line}</div>
          <div className={p.quotaField}><label className={p.quotaLabel}>Over</label><input className={p.quotaInput} value={entry.over} onChange={e => onChange(idx, 'over', e.target.value)} /></div>
          <div className={p.quotaField}><label className={p.quotaLabel}>Under</label><input className={p.quotaInput} value={entry.under} onChange={e => onChange(idx, 'under', e.target.value)} /></div>
          <button className={p.btnOutline} style={{ padding: '4px 8px', color: '#e06c75', borderColor: 'rgba(224,108,117,0.3)' }} onClick={() => onRemove(idx)} title="Remove line">✕</button>
        </div>
      ))}
      {(lines || []).length === 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2px 0' }}>No O/U lines — select and add</div>}
    </div>
  );
}

// (Dynamic O/U lines on the match-edit modal are now driven by `mOuLines` state
// and rendered via <MatchOuLinesEditor>. See STAT_OU_CONFIG above.)


function computeHTWinners(home, away, treatNullAsZero = false) {
  const w = new Set();
  let hFH = home?.result?.firstHalfGoals != null ? Number(home.result.firstHalfGoals) : null;
  let aFH = away?.result?.firstHalfGoals != null ? Number(away.result.firstHalfGoals) : null;
  // When first half is confirmed done, treat missing goals as 0
  if (treatNullAsZero) {
    if (hFH == null) hFH = 0;
    if (aFH == null) aFH = 0;
  }
  if (hFH == null || aFH == null || isNaN(hFH) || isNaN(aFH)) return w;
  const tHT = hFH + aFH;
  if (hFH > aFH) w.add('rhth'); else if (hFH === aFH) w.add('rhtd'); else w.add('rhta');
  if (hFH >= aFH) w.add('htDc1x'); if (hFH !== aFH) w.add('htDc12'); if (aFH >= hFH) w.add('htDcx2');
  [0.5,1.5,2.5].forEach(v => { const k = v.toString().replace('.',''); if (tHT > v) w.add(`htou${k}o`); else w.add(`htou${k}u`); });
  const he = hFH+1; if (he > aFH) w.add('htEh10r1'); else if (he === aFH) w.add('htEh10rx'); else w.add('htEh10r2');
  const ae = aFH+1; if (hFH > ae) w.add('htEh01r1'); else if (hFH === ae) w.add('htEh01rx'); else w.add('htEh01r2');
  if (hFH > 0 && aFH > 0) w.add('htBttsYes'); else w.add('htBttsNo');
  return w;
}

export default function PendingMatches() {
  const api = useApi();
  const toast = useToast();
  const [matches, setMatches] = useState([]);
  const [clasicos, setClasicos] = useState([]);
  // Delete-confirmation modal target. `null` = closed.
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [clasicoToDelete, setClasicoToDelete] = useState(null);
  const [deletingClasico, setDeletingClasico] = useState(false);
  const [deletingMatch, setDeletingMatch] = useState(false);
  const [matchSearch, setMatchSearch] = useState('');
  const [clasicoSearch, setClasicoSearch] = useState('');

  // Pagination
  const PAGE_SIZE = 20;
  const [mPage, setMPage] = useState(1); const [mPages, setMPages] = useState(1); const [mTotal, setMTotal] = useState(0);
  const [cPage, setCPage] = useState(1); const [cPages, setCPages] = useState(1); const [cTotal, setCTotal] = useState(0);

  // Date filter
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState('');

  // Match detail modal state
  const [selMatch, setSelMatch] = useState(null);
  const [mTime, setMTime] = useState(''); const [mTz, setMTz] = useState('+01:00');
  const [mStatus, setMStatus] = useState(null); const [mTeams, setMTeams] = useState([]);
  const [mQuota, setMQuota] = useState({});
  // Dynamic O/U lines per stat category for the match-edit modal.
  const [mOuLines, setMOuLines] = useState(EMPTY_M_OU_LINES());

  // Clasico detail modal state
  const [selClasico, setSelClasico] = useState(null);
  // Winning Markets modal
  const [winModalOpen, setWinModalOpen] = useState(false);
  const [firstHalfDone, setFirstHalfDone] = useState(false);
  const [tofgDone, setTofgDone] = useState(false);
  const [quotaConfig, setQuotaConfig] = useState({});
  const [allDisabled, setAllDisabled] = useState(false);

  // Settings modal (lambdas + margins) for selected clasico
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [lambdas, setLambdas] = useState({});
  const [margins, setMargins] = useState({});

  // Two match result modal slots (A = home team, B = away team) — can be open simultaneously
  const [resultModalA, setResultModalA] = useState(null); // { matchId, label, teams, resultTeams, resultStatus, resultsExist }
  const [resultModalB, setResultModalB] = useState(null);

  const STATUS_FILTER = 'status=draft,pending,started,first_half_done';

  const buildQuery = (page, extra = '') => {
    let q = `?${STATUS_FILTER}&page=${page}&size=${PAGE_SIZE}`;
    if (dateFrom) q += `&createdFrom=${dateFrom}T00:00:00`;
    if (dateTo) q += `&createdTo=${dateTo}T23:59:59`;
    if (extra) q += `&${extra}`;
    return q;
  };

  const loadMatches = useCallback(async (page = 1) => {
    try {
      const r = await api.get(`/v1/match/${buildQuery(page)}`);
      if (r && r.items) { setMatches(r.items); setMPage(r.page); setMPages(r.pages); setMTotal(r.total); }
      else if (Array.isArray(r)) { setMatches(r); } // fallback
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, dateFrom, dateTo]);

  const loadClasicos = useCallback(async (page = 1) => {
    try {
      const r = await api.get(`/v1/clasico/${buildQuery(page)}`);
      if (r && r.items) { setClasicos(r.items); setCPage(r.page); setCPages(r.pages); setCTotal(r.total); }
      else if (Array.isArray(r)) { setClasicos(r); } // fallback
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, dateFrom, dateTo]);

  useEffect(() => {
    loadMatches(1); loadClasicos(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const filteredM = useMemo(() => { if (!matchSearch) return matches; const l = matchSearch.toLowerCase(); return matches.filter(m => m.teams?.some(t => t.name.toLowerCase().includes(l))); }, [matches, matchSearch]);
  const filteredC = useMemo(() => { if (!clasicoSearch) return clasicos; const l = clasicoSearch.toLowerCase(); return clasicos.filter(c => c.teams?.some(t => t.name.toLowerCase().includes(l))); }, [clasicos, clasicoSearch]);

  // Open match detail
  const openMatch = async (match) => {
    try {
      const fresh = await api.get(`/v1/match/${match.id}`);
      const m = fresh || match;
      // Also fetch team results to get timeOfFirstGoal (may not be inline on match response)
      try {
        const results = await api.get(`/v1/team-result/match/${m.id}`);
        if (Array.isArray(results) && results.length > 0 && m.teams) {
          m.teams = m.teams.map((t, idx) => {
            // Try matching: matchTeamId === t.id, or by name, or by index
            const r = results.find(tr => tr.matchTeamId === t.id)
                   || results.find(tr => tr.name && tr.name === t.name)
                   || results[idx]
                   || {};
            return {
              ...t,
              result: {
                ...(t.result || {}),
                minuteOfFirstGoal: r.minuteOfFirstGoal ?? t.result?.minuteOfFirstGoal ?? null,
                timeOfFirstGoal: r.timeOfFirstGoal ?? t.result?.timeOfFirstGoal ?? null,
                timeOfFirstGoalDone: r.timeOfFirstGoalDone ?? t.result?.timeOfFirstGoalDone ?? false,
                firstHalfGoals: r.firstHalfGoals ?? t.result?.firstHalfGoals ?? null,
                fullTimeGoals: r.fullTimeGoals ?? t.result?.fullTimeGoals ?? null,
                ...EXTRA_RESULT_KEYS.reduce((acc, k) => { acc[k] = r[k] ?? t.result?.[k] ?? null; return acc; }, {}),
              }
            };
          });
        }
      } catch { /* team results may not exist yet */ }
      initMatch(m);
    } catch { initMatch(match); }
  };

  const initMatch = (m) => {
    setSelMatch(m);
    if (m.matchTime) {
      setMTz('+01:00');
      const d = new Date(m.matchTime);
      const adj = new Date(d.getTime() + d.getTimezoneOffset() * 60000 + 60 * 60000); // always UTC+1
      const pad = n => n.toString().padStart(2, '0');
      setMTime(`${adj.getFullYear()}-${pad(adj.getMonth() + 1)}-${pad(adj.getDate())}T${pad(adj.getHours())}:${pad(adj.getMinutes())}`);
    }
    setMStatus(m.statusId ?? null);
    const fmt = v => (v != null ? Number(v).toFixed(2) : '');
    if (m.teams) setMTeams(m.teams.map(t => {
      const formatted = { ...t };
      ALL_TEAM_KEYS.forEach(k => { formatted[k] = fmt(t[k]); });
      formatted.result = { minuteOfFirstGoal: t.result?.minuteOfFirstGoal ?? '', timeOfFirstGoal: t.result?.timeOfFirstGoal ?? '', timeOfFirstGoalDone: !!t.result?.timeOfFirstGoalDone, firstHalfGoals: t.result?.firstHalfGoals ?? '', fullTimeGoals: t.result?.fullTimeGoals ?? '', ...pickExtras(t.result, '') };
      return formatted;
    }));
    if (m.quota) { const { id, ...rest } = m.quota; const f = {}; for (const [k,v] of Object.entries(rest)) f[k] = fmt(v); setMQuota(f); }
    // Hydrate dynamic O/U lines per stat category from existing quota
    const parsedOu = EMPTY_M_OU_LINES();
    if (m.quota) {
      for (const [cat, cfg] of Object.entries(STAT_OU_CONFIG)) {
        for (let n = 1; n <= cfg.max; n++) {
          const lineVal = m.quota[`${cfg.prefix}Line${n}`];
          if (lineVal == null) continue;
          parsedOu[cat].push({
            line: Number(lineVal),
            over: m.quota[`${cfg.prefix}${n}o`] != null ? Number(m.quota[`${cfg.prefix}${n}o`]).toFixed(2) : '',
            under: m.quota[`${cfg.prefix}${n}u`] != null ? Number(m.quota[`${cfg.prefix}${n}u`]).toFixed(2) : '',
          });
        }
      }
    }
    setMOuLines(parsedOu);
  };

  const handleMatchUpdate = async () => {
    // Locale-tolerant numeric parse: accept comma-decimal ("1,15") as well as dot.
    const pn = v => {
      if (v === '' || v == null) return null;
      const n = Number(String(v).replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    };
    const isFilled = v => !(v === '' || v == null);
    // Validate required quota keys
    const missingQuota = [...REQUIRED_REAL_QUOTA_KEYS].filter(k => !isFilled(mQuota[k]));
    if (missingQuota.length) { toast.error(`Required match odds missing: ${missingQuota.join(', ')}`); return; }
    // Validate required TT per team
    for (let i = 0; i < mTeams.length; i++) {
      const missing = [...REQUIRED_TEAM_TT_KEYS].filter(k => !isFilled(mTeams[i][k]));
      if (missing.length) { toast.error(`Team ${i + 1} required TT odds missing: ${missing.join(', ')}`); return; }
    }

    // Build quota: required always, optional only when filled. Strip unknown keys.
    const qp = {};
    for (const [k, v] of Object.entries(mQuota)) {
      if (!ALLOWED_REAL_QUOTA_KEYS.has(k)) continue;
      if (REQUIRED_REAL_QUOTA_KEYS.has(k) || isFilled(v)) qp[k] = pn(v);
    }
    // Re-emit dynamic O/U lines from mOuLines (drop any stale keys carried in mQuota first).
    for (const cfg of Object.values(STAT_OU_CONFIG)) {
      for (let n = 1; n <= 3; n++) {
        delete qp[`${cfg.prefix}Line${n}`];
        delete qp[`${cfg.prefix}${n}o`];
        delete qp[`${cfg.prefix}${n}u`];
      }
    }
    for (const [cat, lines] of Object.entries(mOuLines)) {
      const { prefix } = STAT_OU_CONFIG[cat];
      let n = 0;
      for (const entry of lines) {
        if (!isFilled(entry.over) || !isFilled(entry.under)) continue;
        n += 1;
        qp[`${prefix}Line${n}`] = pn(entry.line);
        qp[`${prefix}${n}o`] = pn(entry.over);
        qp[`${prefix}${n}u`] = pn(entry.under);
      }
    }
    const tp = mTeams.map(t => {
      const team = { name: t.name, side: t.side };
      ALL_TEAM_KEYS.forEach(k => {
        if (REQUIRED_TEAM_TT_KEYS.has(k) || isFilled(t[k])) team[k] = pn(t[k]);
      });
      // Only send writable result fields. timeOfFirstGoal / timeOfFirstGoalDone are settlement-side (server-computed).
      team.result = {
        minuteOfFirstGoal: pn(t.result?.minuteOfFirstGoal),
        firstHalfGoals: pn(t.result?.firstHalfGoals),
        fullTimeGoals: pn(t.result?.fullTimeGoals),
        ...EXTRA_RESULT_KEYS.reduce((acc, k) => { acc[k] = pn(t.result?.[k]); return acc; }, {}),
      };
      return team;
    });
    try {
      const r = await api.put(`/v1/match/${selMatch.id}`, { matchTime: mTime ? `${mTime}:00${mTz}` : null, statusId: mStatus, teams: tp, quota: qp });
      toast.success('Match updated'); setMatches(prev => prev.map(m => m.id === selMatch.id ? r : m)); setSelMatch(null); await loadClasicos(cPage);
    } catch (err) { toast.error(err.message); }
  };

  // Delete a pending match (after Modal confirm). Treat 404 as success per soft-delete docs.
  const handleConfirmDeleteMatch = async () => {
    if (!matchToDelete) return;
    setDeletingMatch(true);
    try {
      await api.del(`/v1/match/${matchToDelete.id}`);
      toast.success('Match deleted');
      setMatches(prev => prev.filter(m => m.id !== matchToDelete.id));
      setMatchToDelete(null);
      // Clasicos may depend on this match — refresh both lists from the server.
      await Promise.all([loadMatches(mPage), loadClasicos(cPage)]);
    } catch (e) {
      if (/404/.test(e.message)) {
        setMatches(prev => prev.filter(m => m.id !== matchToDelete.id));
        setMatchToDelete(null);
        await Promise.all([loadMatches(mPage), loadClasicos(cPage)]);
      } else {
        toast.error(e.message || 'Failed to delete match');
      }
    } finally {
      setDeletingMatch(false);
    }
  };

  // Delete a pending clasico — DELETE /v1/clasico/<id>. Treat 404 as already-deleted (success) per soft-delete spec.
  const handleConfirmDeleteClasico = async () => {
    if (!clasicoToDelete) return;
    setDeletingClasico(true);
    try {
      await api.del(`/v1/clasico/${clasicoToDelete.id}`);
      toast.success('Clasico deleted');
      setClasicos(prev => prev.filter(c => c.id !== clasicoToDelete.id));
      setClasicoToDelete(null);
      // Refresh both lists — a clasico delete may also affect related match data.
      await Promise.all([loadMatches(mPage), loadClasicos(cPage)]);
    } catch (e) {
      if (/404/.test(e.message)) {
        setClasicos(prev => prev.filter(c => c.id !== clasicoToDelete.id));
        setClasicoToDelete(null);
        await Promise.all([loadMatches(mPage), loadClasicos(cPage)]);
      } else {
        toast.error(e.message || 'Failed to delete clasico');
      }
    } finally {
      setDeletingClasico(false);
    }
  };

  // Open clasico detail
  const openClasico = async (c) => {
    try {
      const fresh = await api.get(`/v1/clasico/${c.id}`);
      const data = fresh || c;
      setSelClasico(data);
      setFirstHalfDone(data.statusId === 5);
      setTofgDone(!!data.timeOfFirstGoalDone);
      setAllDisabled(!!data.allDisabled);
      setQuotaConfig(data.quotaConfig || {});
    } catch {
      setSelClasico(c);
      setFirstHalfDone(c.statusId === 5);
      setTofgDone(!!c.timeOfFirstGoalDone);
      setAllDisabled(!!c.allDisabled);
      setQuotaConfig(c.quotaConfig || {});
    }
  };

  // Toggle allDisabled master kill-switch
  const handleToggleAllDisabled = async () => {
    if (!selClasico) return;
    const newVal = !allDisabled;
    try {
      await api.put(`/v1/clasico/${selClasico.id}`, {
        allDisabled: newVal,
      });
      setAllDisabled(newVal);
      const fresh = await api.get(`/v1/clasico/${selClasico.id}`);
      if (fresh) { setSelClasico(fresh); setQuotaConfig(fresh.quotaConfig || {}); }
      toast.success(newVal ? 'All quotas disabled' : 'All quotas enabled');
      await loadClasicos(cPage);
    } catch (err) {
      toast.error(err.message || 'Failed to toggle');
    }
  };

  // Toggle individual quota enabled/disabled
  const handleToggleQuota = async (quotaKey) => {
    if (!selClasico) return;
    const configKey = QUOTA_CONFIG_MAP[quotaKey];
    if (!configKey) return;
    const currentVal = quotaConfig[configKey] !== false; // default to true if not set
    const newConfig = { [configKey]: !currentVal };
    try {
      await api.put(`/v1/clasico/${selClasico.id}`, {
        quotaConfig: newConfig,
      });
      setQuotaConfig(prev => ({ ...prev, [configKey]: !currentVal }));
    } catch (err) {
      toast.error(err.message || 'Failed to toggle quota');
    }
  };

  // Toggle entire group enabled/disabled for this clasico
  const handleToggleGroup = async (groupCode) => {
    if (!selClasico || allDisabled) return;
    // Collect all config keys for sections in this group
    const groupSections = QUOTA_SECTIONS.filter(s => s.group === groupCode);
    const configKeys = [];
    groupSections.forEach(s => s.fields.forEach(f => {
      const ck = QUOTA_CONFIG_MAP[f.key];
      if (ck) configKeys.push(ck);
    }));
    if (configKeys.length === 0) return;
    // If any field in the group is enabled, disable all; otherwise enable all
    const anyEnabled = configKeys.some(ck => quotaConfig[ck] !== false);
    const newVal = !anyEnabled;
    const newConfig = {};
    configKeys.forEach(ck => { newConfig[ck] = newVal; });
    try {
      await api.put(`/v1/clasico/${selClasico.id}`, {
        quotaConfig: newConfig,
      });
      setQuotaConfig(prev => ({ ...prev, ...newConfig }));
    } catch (err) {
      toast.error(err.message || 'Failed to toggle group');
    }
  };

  // Check if a group is enabled (at least one field enabled)
  const isGroupEnabled = (groupCode) => {
    const groupSections = QUOTA_SECTIONS.filter(s => s.group === groupCode);
    return groupSections.some(s => s.fields.some(f => {
      const ck = QUOTA_CONFIG_MAP[f.key];
      return ck ? quotaConfig[ck] !== false : true;
    }));
  };

  // Open settings modal — load lambdas & margins from clasico data
  const handleOpenSettings = () => {
    if (!selClasico) return;
    const l = selClasico.lambdas || {};
    setLambdas({
      lambdaHome: l.lambdaHome ?? '',
      lambdaAway: l.lambdaAway ?? '',
      lambdaTotal: l.lambdaTotal ?? '',
      lambdaHomeHt: l.lambdaHomeHt ?? '',
      lambdaAwayHt: l.lambdaAwayHt ?? '',
      lambdaHomeSh: l.lambdaHomeSh ?? '',
      lambdaAwaySh: l.lambdaAwaySh ?? '',
      lambdaHomeCorners: l.lambdaHomeCorners ?? '',
      lambdaAwayCorners: l.lambdaAwayCorners ?? '',
      lambdaHomeHtCorners: l.lambdaHomeHtCorners ?? '',
      lambdaAwayHtCorners: l.lambdaAwayHtCorners ?? '',
      lambdaHomeYc: l.lambdaHomeYc ?? '',
      lambdaAwayYc: l.lambdaAwayYc ?? '',
      lambdaHomeHtYc: l.lambdaHomeHtYc ?? '',
      lambdaAwayHtYc: l.lambdaAwayHtYc ?? '',
      lambdaHomeSot: l.lambdaHomeSot ?? '',
      lambdaAwaySot: l.lambdaAwaySot ?? '',
      lambdaHomeHtSot: l.lambdaHomeHtSot ?? '',
      lambdaAwayHtSot: l.lambdaAwayHtSot ?? '',
    });
    const m = selClasico.margins || {};
    setMargins({
      margin1x2: m.margin1x2 ?? '',
      marginDoubleChance: m.marginDoubleChance ?? '',
      marginFirstGoal: m.marginFirstGoal ?? '',
      marginOu25: m.marginOu25 ?? '',
      marginOu35: m.marginOu35 ?? '',
      marginOu45: m.marginOu45 ?? '',
      marginOu55: m.marginOu55 ?? '',
      marginOu65: m.marginOu65 ?? '',
      marginEh10: m.marginEh10 ?? '',
      marginEh01: m.marginEh01 ?? '',
      marginHt1x2: m.marginHt1x2 ?? '',
      marginHtDoubleChance: m.marginHtDoubleChance ?? '',
      marginHtOu05: m.marginHtOu05 ?? '',
      marginHtOu15: m.marginHtOu15 ?? '',
      marginHtOu25: m.marginHtOu25 ?? '',
      marginHtEh10: m.marginHtEh10 ?? '',
      marginHtEh01: m.marginHtEh01 ?? '',
      marginHtBtts: m.marginHtBtts ?? '',
      // 2H
      marginSh1x2: m.marginSh1x2 ?? '',
      marginShDoubleChance: m.marginShDoubleChance ?? '',
      marginShOu05: m.marginShOu05 ?? '',
      marginShOu15: m.marginShOu15 ?? '',
      marginShOu25: m.marginShOu25 ?? '',
      marginShEh10: m.marginShEh10 ?? '',
      marginShEh01: m.marginShEh01 ?? '',
      marginShBtts: m.marginShBtts ?? '',
      // Special
      marginHsh: m.marginHsh ?? '',
      marginWtn: m.marginWtn ?? '',
      marginDnb: m.marginDnb ?? '',
      // Corners
      marginCornersFt1x2: m.marginCornersFt1x2 ?? '',
      marginCornersFtDoubleChance: m.marginCornersFtDoubleChance ?? '',
      marginCornersFtOu1: m.marginCornersFtOu1 ?? '',
      marginCornersFtOu2: m.marginCornersFtOu2 ?? '',
      marginCornersFtOu3: m.marginCornersFtOu3 ?? '',
      marginCornersFtEh10: m.marginCornersFtEh10 ?? '',
      marginCornersFtEh01: m.marginCornersFtEh01 ?? '',
      marginCornersFtEt: m.marginCornersFtEt ?? '',
      marginCornersHt1x2: m.marginCornersHt1x2 ?? '',
      marginCornersHtDoubleChance: m.marginCornersHtDoubleChance ?? '',
      marginCornersHtOu1: m.marginCornersHtOu1 ?? '',
      marginCornersHtOu2: m.marginCornersHtOu2 ?? '',
      marginCornersHtOu3: m.marginCornersHtOu3 ?? '',
      marginCornersHtEh10: m.marginCornersHtEh10 ?? '',
      marginCornersHtEh01: m.marginCornersHtEh01 ?? '',
      marginCornersHtBt: m.marginCornersHtBt ?? '',
      // YC
      marginYcFt1x2: m.marginYcFt1x2 ?? '',
      marginYcFtDoubleChance: m.marginYcFtDoubleChance ?? '',
      marginYcFtOu1: m.marginYcFtOu1 ?? '',
      marginYcFtOu2: m.marginYcFtOu2 ?? '',
      marginYcFtOu3: m.marginYcFtOu3 ?? '',
      marginYcFtEh10: m.marginYcFtEh10 ?? '',
      marginYcFtEh01: m.marginYcFtEh01 ?? '',
      marginYcFtEt: m.marginYcFtEt ?? '',
      marginYcHt1x2: m.marginYcHt1x2 ?? '',
      marginYcHtDoubleChance: m.marginYcHtDoubleChance ?? '',
      marginYcHtOu1: m.marginYcHtOu1 ?? '',
      marginYcHtOu2: m.marginYcHtOu2 ?? '',
      marginYcHtOu3: m.marginYcHtOu3 ?? '',
      marginYcHtEh10: m.marginYcHtEh10 ?? '',
      marginYcHtEh01: m.marginYcHtEh01 ?? '',
      marginYcHtEt: m.marginYcHtEt ?? '',
      // SOT
      marginSotFt1x2: m.marginSotFt1x2 ?? '',
      marginSotFtDoubleChance: m.marginSotFtDoubleChance ?? '',
      marginSotFtOu1: m.marginSotFtOu1 ?? '',
      marginSotFtOu2: m.marginSotFtOu2 ?? '',
      marginSotFtOu3: m.marginSotFtOu3 ?? '',
      marginSotFtEh10: m.marginSotFtEh10 ?? '',
      marginSotFtEh01: m.marginSotFtEh01 ?? '',
      marginSotFtEt: m.marginSotFtEt ?? '',
      marginSotHt1x2: m.marginSotHt1x2 ?? '',
      marginSotHtDoubleChance: m.marginSotHtDoubleChance ?? '',
      marginSotHtOu1: m.marginSotHtOu1 ?? '',
      marginSotHtOu2: m.marginSotHtOu2 ?? '',
      marginSotHtOu3: m.marginSotHtOu3 ?? '',
      marginSotHtEh10: m.marginSotHtEh10 ?? '',
      marginSotHtEh01: m.marginSotHtEh01 ?? '',
      marginSotHtBt: m.marginSotHtBt ?? '',
    });
    setSettingsOpen(true);
  };

  const handleSaveLambdas = async () => {
    if (!selClasico) return;
    const pn = v => (v === '' || v == null ? null : Number(v));
    const body = {};
    for (const [k, v] of Object.entries(lambdas)) body[k] = pn(v);
    try {
      await api.put(`/v1/clasico/${selClasico.id}/lambdas`, body);
      toast.success('Lambdas updated — recalculating…');
      try { await api.post(`/v1/clasico/${selClasico.id}/recalculate`); } catch { /* recalc may be no-op */ }
      const fresh = await api.get(`/v1/clasico/${selClasico.id}`);
      if (fresh) setSelClasico(fresh);
    } catch (err) { toast.error(err.message || 'Failed to update lambdas'); }
  };

  const handleSaveMargins = async () => {
    if (!selClasico) return;
    const pn = v => (v === '' || v == null ? null : Number(v));
    const body = {};
    for (const [k, v] of Object.entries(margins)) body[k] = pn(v);
    try {
      await api.put(`/v1/clasico/${selClasico.id}/margins`, body);
      toast.success('Margins updated — recalculating…');
      try { await api.post(`/v1/clasico/${selClasico.id}/recalculate`); } catch { /* recalculate may not exist yet */ }
      const fresh = await api.get(`/v1/clasico/${selClasico.id}`);
      if (fresh) setSelClasico(fresh);
    } catch (err) { toast.error(err.message || 'Failed to update margins'); }
  };

  // Toggle first half done
  const handleFirstHalfToggle = async () => {
    if (!selClasico) return;
    const newVal = !firstHalfDone;
    const newStatusId = newVal ? 5 : 2; // 5 = first_half_done, 2 = started
    try {
      await api.put(`/v1/clasico/${selClasico.id}`, {
        statusId: newStatusId,
      });
      setFirstHalfDone(newVal);
      // Refresh clasico data
      const fresh = await api.get(`/v1/clasico/${selClasico.id}`);
      if (fresh) setSelClasico(fresh);
      toast.success(newVal ? 'First half marked as done' : 'First half status reverted');
      await loadClasicos(cPage);
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    }
  };

  // Toggle time of first goal done
  const handleTofgToggle = async () => {
    if (!selClasico) return;
    const newVal = !tofgDone;
    try {
      await api.put(`/v1/clasico/${selClasico.id}`, { timeOfFirstGoalDone: newVal });
      setTofgDone(newVal);
      const fresh = await api.get(`/v1/clasico/${selClasico.id}`);
      if (fresh) setSelClasico(fresh);
      toast.success(newVal ? 'Time of first goal marked done' : 'Time of first goal reverted');
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
  };

  // Open match result modal from clasico team click — resolves original match via matchTeamId
  const openResultSlot = async (team, slot) => {
    if (!team) return;
    const setter = slot === 'A' ? setResultModalA : setResultModalB;
    const current = slot === 'A' ? resultModalA : resultModalB;

    // Fetch all matches (unfiltered) to resolve the original match
    const res = await api.get('/v1/match/?size=500');
    const allM = res?.items || (Array.isArray(res) ? res : []);
    if (!allM.length) { toast.error('Could not load matches'); return; }

    // Try to resolve by matchTeamId first (match team entry id)
    let found = allM.find(m => m.teams?.some(t => t.id === team.matchTeamId));

    // Fallback: matchTeamId might reference the match itself
    if (!found) {
      found = allM.find(m => m.id === team.matchTeamId);
    }

    // Fallback: try matching where match has a team with the same name
    // Pick the match whose status is not finished, or the most recent one
    if (!found) {
      const candidates = allM.filter(m => m.teams?.some(t => t.name === team.name));
      if (candidates.length === 1) {
        found = candidates[0];
      } else if (candidates.length > 1) {
        // Prefer non-finished match, then highest id
        const nonFinished = candidates.filter(m => m.statusId !== 3);
        found = (nonFinished.length > 0 ? nonFinished : candidates).sort((a, b) => b.id - a.id)[0];
      }
    }

    if (!found) { toast.error('Could not find original match'); return; }

    // Toggle off if same match already open in this slot
    if (current?.matchId === found.id) { setter(null); return; }

    const teamNames = found.teams.map(t => t.name);
    const matchTeamIds = found.teams.map(t => t.id);
    const label = `Match #${found.id} — ${teamNames.join(' vs ')}`;
    const statusObj = STATUSES.find(s => s.id === found.statusId);

    let rTeams = [];
    let exists = false;
    try {
      const res = await api.get(`/v1/team-result/match/${found.id}`);
      if (res && Array.isArray(res) && res.length > 0) {
        exists = true;
        rTeams = res.map((t, i) => ({ ...t, name: t.name || found.teams[i]?.name || `Team ${i + 1}`, matchTeamId: t.matchTeamId || matchTeamIds[i], timeOfFirstGoal: t.timeOfFirstGoal ?? '', ...pickExtras(t, '') }));
      }
    } catch { /* ignore */ }

    if (!exists) {
      rTeams = found.teams.map(t => ({ matchTeamId: t.id, name: t.name, minuteOfFirstGoal: '', timeOfFirstGoal: '', firstHalfGoals: '', fullTimeGoals: '', ...pickExtras(null, '') }));
    }

    setter({ matchId: found.id, label, teamNames, matchTeamIds, resultTeams: rTeams, resultStatus: statusObj?.code || '', resultsExist: exists });
  };

  const updateResultSlot = (slot, field, value) => {
    const setter = slot === 'A' ? setResultModalA : setResultModalB;
    setter(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const updateResultSlotTeam = (slot, idx, field, value) => {
    const setter = slot === 'A' ? setResultModalA : setResultModalB;
    setter(prev => {
      if (!prev) return prev;
      const teams = prev.resultTeams.map((t, i) => i === idx ? { ...t, [field]: value } : t);
      return { ...prev, resultTeams: teams };
    });
  };

  const handleResultUpdate = async (slot) => {
    const modal = slot === 'A' ? resultModalA : resultModalB;
    const setter = slot === 'A' ? setResultModalA : setResultModalB;
    if (!modal) return;
    const pn = v => (v === '' || v == null ? null : Number(v));

    if (modal.resultsExist) {
      try {
        await api.put(`/v1/team-result/match/${modal.matchId}`, {
          matchStatus: modal.resultStatus || null,
          results: modal.resultTeams.map(t => ({ name: t.name, minuteOfFirstGoal: pn(t.minuteOfFirstGoal), timeOfFirstGoal: t.timeOfFirstGoal || null, firstHalfGoals: pn(t.firstHalfGoals), fullTimeGoals: pn(t.fullTimeGoals), ...EXTRA_RESULT_KEYS.reduce((acc, k) => { acc[k] = pn(t[k]); return acc; }, {}) })),
        });
        toast.success('Result updated');
        if (selClasico) { const fresh = await api.get(`/v1/clasico/${selClasico.id}`); if (fresh) setSelClasico(fresh); }
        await loadClasicos(cPage);
      } catch (err) { toast.error(err.message); }
    } else {
      const filled = modal.resultTeams.filter(t => t.timeOfFirstGoal !== '' || t.firstHalfGoals !== '' || t.fullTimeGoals !== '' || EXTRA_RESULT_KEYS.some(k => t[k] !== '' && t[k] != null));
      for (const t of filled) {
        try { await api.post('/v1/team-result/', { matchId: modal.matchId, matchTeamId: t.matchTeamId, minuteOfFirstGoal: pn(t.minuteOfFirstGoal), timeOfFirstGoal: t.timeOfFirstGoal || null, firstHalfGoals: pn(t.firstHalfGoals), fullTimeGoals: pn(t.fullTimeGoals), ...EXTRA_RESULT_KEYS.reduce((acc, k) => { acc[k] = pn(t[k]); return acc; }, {}) }); } catch {}
      }
      toast.success('Results created');
      setter(prev => prev ? { ...prev, resultsExist: true } : prev);
      if (selClasico) { const fresh = await api.get(`/v1/clasico/${selClasico.id}`); if (fresh) setSelClasico(fresh); }
      await loadClasicos(cPage);
    }
  };

  const homeM = mTeams.find(t => getSideLabel(t) === 'Home');
  const awayM = mTeams.find(t => getSideLabel(t) === 'Away');
  const homeC = selClasico?.teams?.find(t => getSideLabel(t) === 'Home');
  const awayC = selClasico?.teams?.find(t => getSideLabel(t) === 'Away');
  const htWinners = firstHalfDone ? computeHTWinners(homeC, awayC, true) : new Set();

  // Compute first goal winner when tofgDone OR firstHalfDone (at HT we already know who scored first)
  const fgWinners = (() => {
    const w = new Set();
    if (!tofgDone && !firstHalfDone) return w;
    const ht = parseGoalTime(homeC?.result?.timeOfFirstGoal);
    const at = parseGoalTime(awayC?.result?.timeOfFirstGoal);
    if (ht == null && at == null) { w.add('fgNogoal'); }
    else if (ht != null && at != null) { if (ht <= at) w.add('fgHome'); else w.add('fgAway'); }
    else if (ht != null) { w.add('fgHome'); }
    else { w.add('fgAway'); }
    return w;
  })();

  const fmtInt = v => (v == null ? '—' : v);
  const getScore = t => { const r = t?.result; if (r?.fullTimeGoals != null) return r.fullTimeGoals; if (r?.firstHalfGoals != null) return r.firstHalfGoals; return '—'; };

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle}>Pending Matches</h1>
        <p className={p.pageSubtitle}>Manage active matches and clasicos</p>
      </div>

      {/* Date range filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setShowDateFilter(v => !v)} className={p.btnOutline} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', padding: '5px 12px' }}>
          <FiFilter size={13} /> {showDateFilter ? 'Hide Filters' : 'Date Filter'}
        </button>
        {showDateFilter && <>
          <input type="date" className={p.formInput} style={{ width: 150, fontSize: '0.78rem', padding: '5px 8px' }} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMPage(1); setCPage(1); }} />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>to</span>
          <input type="date" className={p.formInput} style={{ width: 150, fontSize: '0.78rem', padding: '5px 8px' }} value={dateTo} onChange={e => { setDateTo(e.target.value); setMPage(1); setCPage(1); }} />
          {(dateFrom || dateTo) && <button className={p.btnOutline} style={{ fontSize: '0.72rem', padding: '4px 10px' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>}
        </>}
      </div>

      <div className={p.columns}>
        <div>
          <div className={p.colHeader}>
            <h3 className={p.colTitle}>Matches</h3>
            <div style={{ position: 'relative' }}><FiSearch style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }} /><input className={p.searchInput} style={{ paddingLeft: 28, minWidth: 160 }} placeholder="Search…" value={matchSearch} onChange={e => setMatchSearch(e.target.value)} /></div>
          </div>
          <div className={p.list}>
            {filteredM.length > 0 ? filteredM.map(m => <MatchCard key={m.id} match={m} onItemClick={openMatch} onDelete={setMatchToDelete} />) : <div className={p.emptyState}><div className={p.emptyText}>No pending matches</div></div>}
          </div>
          <Pagination page={mPage} pages={mPages} total={mTotal} size={PAGE_SIZE} onPageChange={pg => { setMPage(pg); loadMatches(pg); }} />
        </div>
        <div>
          <div className={p.colHeader}>
            <h3 className={p.colTitle}>Clasicos</h3>
            <div style={{ position: 'relative' }}><FiSearch style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }} /><input className={p.searchInput} style={{ paddingLeft: 28, minWidth: 160 }} placeholder="Search…" value={clasicoSearch} onChange={e => setClasicoSearch(e.target.value)} /></div>
          </div>
          <div className={p.list}>
            {filteredC.length > 0 ? filteredC.map(c => <MatchCard key={c.id} match={c} onItemClick={openClasico} onDelete={setClasicoToDelete} />) : <div className={p.emptyState}><div className={p.emptyText}>No pending clasicos</div></div>}
          </div>
          <Pagination page={cPage} pages={cPages} total={cTotal} size={PAGE_SIZE} onPageChange={pg => { setCPage(pg); loadClasicos(pg); }} />
        </div>
      </div>

      {/* Delete-confirmation modal for pending match items. */}
      <Modal
        open={!!matchToDelete}
        onClose={() => !deletingMatch && setMatchToDelete(null)}
        title="Delete match?"
        footer={<>
          <button className={p.btnOutline} onClick={() => setMatchToDelete(null)} disabled={deletingMatch} style={{ marginRight: 8 }}>Cancel</button>
          <button className={p.btnPrimary} onClick={handleConfirmDeleteMatch} disabled={deletingMatch}
            style={{ background: '#e06c75', borderColor: '#e06c75' }}>
            {deletingMatch ? 'Deleting…' : 'Delete'}
          </button>
        </>}
      >
        {matchToDelete && (
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.5 }}>
            Are you sure you want to delete this match?<br />
            <strong style={{ color: 'var(--text-primary)' }}>
              {matchToDelete.teams?.find(t => /home/i.test(String(t.side)))?.name || matchToDelete.teams?.[0]?.name || '—'}
              {' vs '}
              {matchToDelete.teams?.find(t => /away/i.test(String(t.side)))?.name || matchToDelete.teams?.[1]?.name || '—'}
            </strong>
            <br />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>This action cannot be undone from the UI.</span>
          </p>
        )}
      </Modal>

      {/* Delete-confirmation modal for pending clasico items. */}
      <Modal
        open={!!clasicoToDelete}
        onClose={() => !deletingClasico && setClasicoToDelete(null)}
        title="Delete clasico?"
        footer={<>
          <button className={p.btnOutline} onClick={() => setClasicoToDelete(null)} disabled={deletingClasico} style={{ marginRight: 8 }}>Cancel</button>
          <button className={p.btnPrimary} onClick={handleConfirmDeleteClasico} disabled={deletingClasico}
            style={{ background: '#e06c75', borderColor: '#e06c75' }}>
            {deletingClasico ? 'Deleting…' : 'Delete'}
          </button>
        </>}
      >
        {clasicoToDelete && (
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.5 }}>
            Are you sure you want to delete this clasico?<br />
            <strong style={{ color: 'var(--text-primary)' }}>
              {clasicoToDelete.teams?.find(t => /home/i.test(String(t.side)))?.name || clasicoToDelete.teams?.[0]?.name || '—'}
              {' vs '}
              {clasicoToDelete.teams?.find(t => /away/i.test(String(t.side)))?.name || clasicoToDelete.teams?.[1]?.name || '—'}
            </strong>
            <br />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>This action cannot be undone from the UI.</span>
          </p>
        )}
      </Modal>

      {/* Match Detail Modal */}
      <Modal open={!!selMatch} onClose={() => setSelMatch(null)} title="Match Detail" wide
        footer={<button className={p.btnPrimary} onClick={handleMatchUpdate}>Update</button>}>
        {selMatch && <>
          <div className={p.scoreboard}>
            <div className={p.scoreTeam}><span className={p.scoreTeamName}>{homeM?.name}</span><span className={p.scoreTeamSide}>Home</span></div>
            <div className={p.scoreMain}>vs</div>
            <div className={p.scoreTeam}><span className={p.scoreTeamName}>{awayM?.name}</span><span className={p.scoreTeamSide}>Away</span></div>
          </div>
          <div className={p.formRow} style={{ marginBottom: 16 }}>
            <div className={p.formGroup}><label className={p.formLabel}>Date & Time</label><input className={p.formInput} type="datetime-local" value={mTime} onChange={e => setMTime(e.target.value)} /></div>
            <div className={p.formGroup}><label className={p.formLabel}>Timezone</label><select className={p.formSelect} value={mTz} onChange={e => setMTz(e.target.value)}>{TZ_OPTIONS.map(tz => <option key={tz} value={tz}>UTC{tz}</option>)}</select></div>
          </div>
          <div className={p.formGroup}><label className={p.formLabel}>Status</label><select className={p.formSelect} value={mStatus ?? ''} onChange={e => setMStatus(e.target.value === '' ? null : Number(e.target.value))}><option value="">— none —</option>{STATUSES.map(s => <option key={s.id} value={s.id}>{s.code}</option>)}</select></div>
          {[homeM, awayM].filter(Boolean).map(team => (
            <div key={team.id} style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: 8 }}>{getSideLabel(team)} — {team.name}</h4>
              <div className={p.quotaGrid}>
                {TT_FIELDS.map(f => <div key={f.key} className={p.quotaField}><label className={p.quotaLabel}>{f.l}</label><input className={p.quotaInput} value={team[f.key] ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, [f.key]: e.target.value } : t))} /></div>)}
              </div>
              {/* Stat TT fields */}
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Stat Team Totals</div>
                <div className={p.quotaGrid}>
                  {STAT_TT_FIELDS.map(f => (
                    <React.Fragment key={f.lineKey}>
                      <div className={p.quotaField}><label className={p.quotaLabel}>{f.l} Line</label><input className={p.quotaInput} value={team[f.lineKey] ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, [f.lineKey]: e.target.value } : t))} /></div>
                      <div className={p.quotaField}><label className={p.quotaLabel}>{f.l} O</label><input className={p.quotaInput} value={team[f.oKey] ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, [f.oKey]: e.target.value } : t))} /></div>
                      <div className={p.quotaField}><label className={p.quotaLabel}>{f.l} U</label><input className={p.quotaInput} value={team[f.uKey] ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, [f.uKey]: e.target.value } : t))} /></div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div className={p.resultSection} style={{ marginTop: 10 }}>
                <div className={p.resultLabel}>Result — {team.name}</div>
                <div className={p.resultFields}>
                  <div className={p.fieldGroup}><label className={p.fieldLabel}>Time 1st Goal <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>(mm:ss.ms)</span></label><input className={p.resultInput} placeholder="e.g. 12:34.567" value={team.result?.timeOfFirstGoal ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, result: { ...t.result, timeOfFirstGoal: e.target.value } } : t))} /></div>
                  <div className={p.fieldGroup}><label className={p.fieldLabel}>FH Goals</label><input className={p.resultInput} value={team.result?.firstHalfGoals ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, result: { ...t.result, firstHalfGoals: e.target.value } } : t))} /></div>
                  <div className={p.fieldGroup}><label className={p.fieldLabel}>FT Goals</label><input className={p.resultInput} value={team.result?.fullTimeGoals ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, result: { ...t.result, fullTimeGoals: e.target.value } } : t))} /></div>
                  {EXTRA_RESULT_FIELDS.map(f => (
                    <div key={f.key} className={p.fieldGroup}><label className={p.fieldLabel}>{f.label}</label><input className={p.resultInput} value={team.result?.[f.key] ?? ''} onChange={e => setMTeams(prev => prev.map(t => t.id === team.id ? { ...t, result: { ...t.result, [f.key]: e.target.value } } : t))} /></div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {MATCH_QUOTA_SECTIONS.map(sec => <div key={sec.label} style={{ marginBottom: 16 }}><h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>{sec.label}</h4><div className={p.quotaGrid}>{sec.fields.map(f => <div key={f.key} className={p.quotaField}><label className={p.quotaLabel}>{f.l}</label><input className={p.quotaInput} value={mQuota[f.key] ?? ''} onChange={e => setMQuota(prev => ({ ...prev, [f.key]: e.target.value }))} /></div>)}</div></div>)}
          {STAT_CATEGORIES_1X2.map(({ cat, keys }) => {
            const cfg = STAT_OU_CONFIG[cat];
            return (
              <div key={cat} style={{ marginBottom: 16, padding: 12, background: 'var(--bg-primary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                <h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>{cfg.label}</h4>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>1X2</div>
                <div className={p.quotaGrid} style={{ marginBottom: 10 }}>
                  <div className={p.quotaField}><label className={p.quotaLabel}>Home</label><input className={p.quotaInput} value={mQuota[keys[0]] ?? ''} onChange={e => setMQuota(prev => ({ ...prev, [keys[0]]: e.target.value }))} /></div>
                  <div className={p.quotaField}><label className={p.quotaLabel}>Draw</label><input className={p.quotaInput} value={mQuota[keys[1]] ?? ''} onChange={e => setMQuota(prev => ({ ...prev, [keys[1]]: e.target.value }))} /></div>
                  <div className={p.quotaField}><label className={p.quotaLabel}>Away</label><input className={p.quotaInput} value={mQuota[keys[2]] ?? ''} onChange={e => setMQuota(prev => ({ ...prev, [keys[2]]: e.target.value }))} /></div>
                </div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Over/Under Lines</div>
                <MatchOuLinesEditor catKey={cat} config={cfg} lines={mOuLines[cat]} setLines={setMOuLines} />
              </div>
            );
          })}
        </>}
      </Modal>

      {/* Clasico Detail + Side-by-side Result Modals
       * Backdrop click is intentionally a NO-OP — the panel can only be closed
       * by the explicit ✕ button in the header (see Modal.js for the same rule). */}
      {selClasico && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 0, padding: '40px 16px', overflowY: 'auto' }}>

          {/* Result modal A (left) */}
          {resultModalA && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '14px 0 0 14px', boxShadow: '0 16px 48px rgba(0,0,0,0.5)', width: 380, maxHeight: '85vh', display: 'flex', flexDirection: 'column', flexShrink: 0, animation: 'slideUp 0.2s ease' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>{resultModalA.label}</h3>
                <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem' }} onClick={() => setResultModalA(null)}>✕</button>
              </div>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                <div className={p.formGroup}><label className={p.formLabel}>Match Status</label><select className={p.formSelect} value={resultModalA.resultStatus} onChange={e => updateResultSlot('A', 'resultStatus', e.target.value)}><option value="">— none —</option>{STATUSES.map(s => <option key={s.id} value={s.code}>{s.code}</option>)}</select></div>
                {resultModalA.resultTeams.map((t, i) => (
                  <div key={i} className={p.resultSection} style={{ marginBottom: 10, background: i === 0 ? 'rgba(108,140,191,0.06)' : 'rgba(212,160,82,0.06)', borderColor: i === 0 ? 'rgba(108,140,191,0.15)' : 'rgba(212,160,82,0.15)' }}>
                    <div className={p.resultLabel}>{t.name}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className={p.fieldGroup}><label className={p.fieldLabel}>Time 1st Goal <span style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>(mm:ss.ms)</span></label><input className={p.resultInput} placeholder="e.g. 12:34.567" value={t.timeOfFirstGoal ?? ''} onChange={e => updateResultSlotTeam('A', i, 'timeOfFirstGoal', e.target.value)} /></div>
                      <div className={p.fieldGroup}><label className={p.fieldLabel}>FH Goals</label><input className={p.resultInput} value={t.firstHalfGoals ?? ''} onChange={e => updateResultSlotTeam('A', i, 'firstHalfGoals', e.target.value)} /></div>
                      <div className={p.fieldGroup}><label className={p.fieldLabel}>FT Goals</label><input className={p.resultInput} value={t.fullTimeGoals ?? ''} onChange={e => updateResultSlotTeam('A', i, 'fullTimeGoals', e.target.value)} /></div>
                      {EXTRA_RESULT_FIELDS.map(f => (
                        <div key={f.key} className={p.fieldGroup}><label className={p.fieldLabel}>{f.label}</label><input className={p.resultInput} value={t[f.key] ?? ''} onChange={e => updateResultSlotTeam('A', i, f.key, e.target.value)} /></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
                <button className={p.btnPrimary} onClick={() => handleResultUpdate('A')}>{resultModalA.resultsExist ? 'Update' : 'Create'}</button>
              </div>
            </div>
          )}

          {/* Clasico Preview (center) */}
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: `${resultModalA ? '0' : '14px'} ${resultModalB ? '0' : '14px'} ${resultModalB ? '0' : '14px'} ${resultModalA ? '0' : '14px'}`, boxShadow: '0 16px 48px rgba(0,0,0,0.5)', width: 600, maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', flexShrink: 0, animation: 'slideUp 0.2s ease' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Clasico Preview</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setWinModalOpen(true)} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600, color: '#73c991', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><FiAward size={12} /> Winning Markets</button>
                <button onClick={handleOpenSettings} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>⚙ Settings</button>
                <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem' }} onClick={() => { setSelClasico(null); setResultModalA(null); setResultModalB(null); setSettingsOpen(false); }}>✕</button>
              </div>
            </div>
            <div style={{ padding: '24px 30px 24px 24px', overflowY: 'auto', flex: 1 }}>
              <div className={p.scoreboard}>
                <div className={p.scoreTeam} style={{ cursor: 'pointer' }} onClick={() => openResultSlot(homeC, 'A')}>
                  <span className={p.scoreTeamName}>{homeC?.name}</span>
                  <span className={p.scoreTeamSide}>Home — click for result</span>
                </div>
                <div style={{ textAlign: 'center' }}><div className={p.scoreMain}>{getScore(homeC)} : {getScore(awayC)}</div></div>
                <div className={p.scoreTeam} style={{ cursor: 'pointer' }} onClick={() => openResultSlot(awayC, 'B')}>
                  <span className={p.scoreTeamName}>{awayC?.name}</span>
                  <span className={p.scoreTeamSide}>Away — click for result</span>
                </div>
              </div>
              {[homeC, awayC].filter(Boolean).map(t => <div key={t.id} className={p.resultSection}><div className={p.resultLabel}>{getSideLabel(t)} — {t.name}</div><div style={{ display: 'flex', gap: 16, fontSize: '0.82rem', flexWrap: 'wrap', alignItems: 'center' }}><span>Time 1st Goal: <strong>{(t.result?.timeOfFirstGoal != null && secsToTimeStr(t.result.timeOfFirstGoal)) || '—'}</strong></span><span>FH: {fmtInt(t.result?.firstHalfGoals)}</span><span>FT: {fmtInt(t.result?.fullTimeGoals)}</span>{t.result?.timeOfFirstGoalDone && <span className={`${p.badge} ${p.badgeFinished}`} style={{ fontSize: '0.6rem' }}>1st Goal Done</span>}</div></div>)}

              {/* Match times */}
              {(selClasico.matchTimeA || selClasico.matchTimeB) && (
                <div style={{ display: 'flex', gap: 16, margin: '12px 0', padding: '10px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                  {selClasico.matchTimeA && <span><strong>Match A:</strong> {new Date(selClasico.matchTimeA).toLocaleString()}</span>}
                  {selClasico.matchTimeB && <span><strong>Match B:</strong> {new Date(selClasico.matchTimeB).toLocaleString()}</span>}
                  {selClasico.matchTime && <span><strong>Effective:</strong> {new Date(selClasico.matchTime).toLocaleString()}</span>}
                </div>
              )}

              {/* First Half Done toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0', padding: '12px 16px', background: firstHalfDone ? 'rgba(115,201,145,0.08)' : 'var(--bg-primary)', border: `1px solid ${firstHalfDone ? 'rgba(115,201,145,0.25)' : 'var(--border-color)'}`, borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleFirstHalfToggle}>
                <div style={{ width: 40, height: 22, borderRadius: 11, background: firstHalfDone ? '#73c991' : 'var(--border-color)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: firstHalfDone ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: firstHalfDone ? '#73c991' : 'var(--text-primary)' }}>First Half Done</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{firstHalfDone ? 'HT settlements are highlighted below' : 'Toggle to settle half-time markets'}</div>
                </div>
              </div>

              {/* Time of First Goal Done toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 16px', padding: '12px 16px', background: tofgDone ? 'rgba(212,160,82,0.08)' : 'var(--bg-primary)', border: `1px solid ${tofgDone ? 'rgba(212,160,82,0.25)' : 'var(--border-color)'}`, borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleTofgToggle}>
                <div style={{ width: 40, height: 22, borderRadius: 11, background: tofgDone ? '#d4a052' : 'var(--border-color)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: tofgDone ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: tofgDone ? '#d4a052' : 'var(--text-primary)' }}>Time of First Goal Done</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{tofgDone ? 'First goal market is settled — winner shown below' : 'Mark when first goal timing is decided'}</div>
                </div>
              </div>

              {/* First Goal Winner Highlight */}
              {(tofgDone || firstHalfDone) && (() => {
                const htRaw = homeC?.result?.timeOfFirstGoal;
                const atRaw = awayC?.result?.timeOfFirstGoal;
                const ht = parseGoalTime(htRaw);
                const at = parseGoalTime(atRaw);
                if (ht == null && at == null) return (
                  <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 8, background: 'rgba(212,160,82,0.08)', border: '1px solid rgba(212,160,82,0.2)' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#d4a052' }}>⚽ No Goal — both teams</div>
                  </div>
                );
                const winner = (ht != null && at != null) ? (ht < at ? 'Home' : at < ht ? 'Away' : 'Same time') : (ht != null ? 'Home' : 'Away');
                const winnerName = winner === 'Home' ? homeC?.name : winner === 'Away' ? awayC?.name : winner;
                return (
                  <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 8, background: 'rgba(115,201,145,0.08)', border: '1px solid rgba(115,201,145,0.25)' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#73c991' }}>⚽ First Goal Winner: {winnerName}</div>
                    <div style={{ display: 'flex', gap: 20, fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                      <span>{homeC?.name}: {htRaw != null ? secsToTimeStr(htRaw) : 'No goal'}</span>
                      <span>{awayC?.name}: {atRaw != null ? secsToTimeStr(atRaw) : 'No goal'}</span>
                    </div>
                  </div>
                );
              })()}

              {/* All Disabled master toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 16px', padding: '12px 16px', background: allDisabled ? 'rgba(224,108,117,0.08)' : 'var(--bg-primary)', border: `1px solid ${allDisabled ? 'rgba(224,108,117,0.25)' : 'var(--border-color)'}`, borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleToggleAllDisabled}>
                <div style={{ width: 40, height: 22, borderRadius: 11, background: allDisabled ? '#e06c75' : 'var(--border-color)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: allDisabled ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: allDisabled ? '#e06c75' : 'var(--text-primary)' }}>Disable All Quotas</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{allDisabled ? 'All markets are hidden for this match' : 'Master kill-switch for all quotas'}</div>
                </div>
              </div>

              {allDisabled && (
                <div style={{ padding: '16px', background: 'rgba(224,108,117,0.06)', border: '1px solid rgba(224,108,117,0.15)', borderRadius: 10, marginBottom: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#e06c75', marginBottom: 4 }}>⚠ All Quotas Disabled</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Toggle off "Disable All Quotas" to manage individual markets</div>
                </div>
              )}

              {QUOTA_SECTIONS.map((sec, secIdx) => {
                const groupEnabled = !allDisabled && isGroupEnabled(sec.group);
                const isFirstOfGroup = secIdx === 0 || QUOTA_SECTIONS[secIdx - 1].group !== sec.group;
                const visibleFields = sec.fields.filter(f => { const v = selClasico.quota?.[f.key]; return v != null && v >= 1; });
                if (visibleFields.length === 0) return null;
                return (
                  <div key={sec.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>{sec.label}</h4>
                      {isFirstOfGroup && !allDisabled && (
                        <div
                          onClick={(e) => { e.stopPropagation(); handleToggleGroup(sec.group); }}
                          style={{ width: 34, height: 18, borderRadius: 9, background: groupEnabled ? '#73c991' : '#e06c75', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}
                          title={groupEnabled ? `Disable ${sec.group.replace(/_/g, ' ')}` : `Enable ${sec.group.replace(/_/g, ' ')}`}
                        >
                          <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: groupEnabled ? 18 : 2, transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                        </div>
                      )}
                    </div>
                    <div className={p.quotaGrid}>
                      {visibleFields.map(f => {
                        const isWin = (firstHalfDone && htWinners.has(f.key)) || fgWinners.has(f.key);
                        const configKey = QUOTA_CONFIG_MAP[f.key];
                        const isEnabled = !allDisabled && (configKey ? quotaConfig[configKey] !== false : true);
                        const isDisabled = allDisabled || !isEnabled;
                        return (
                          <div key={f.key} className={p.quotaField} style={{
                            ...(isWin && !isDisabled ? { background: 'rgba(115,201,145,0.1)', borderRadius: 6, border: '1px solid rgba(115,201,145,0.25)' } : {}),
                            ...(isDisabled ? { opacity: 0.35 } : {}),
                            position: 'relative',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                              <label className={p.quotaLabel} style={isWin && !isDisabled ? { color: '#73c991' } : {}}>{f.l}</label>
                              {!allDisabled && configKey && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); handleToggleQuota(f.key); }}
                                  style={{ width: 28, height: 16, borderRadius: 8, background: isEnabled ? '#73c991' : 'var(--border-color)', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}
                                >
                                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: isEnabled ? 14 : 2, transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                                </div>
                              )}
                            </div>
                            <div className={p.quotaValue} style={isWin && !isDisabled ? { color: '#73c991', fontWeight: 700 } : {}}>{formatQuota(selClasico.quota?.[f.key])}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Dynamic O/U sections for stat categories */}
              {buildDynamicOuSections(selClasico?.quota).map((sec) => {
                const visibleFields = sec.fields.filter(f => { const v = selClasico.quota?.[f.key]; return v != null && v >= 1; });
                if (visibleFields.length === 0) return null;
                return (
                  <div key={sec.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>{sec.label}</h4>
                    </div>
                    <div className={p.quotaGrid}>
                      {visibleFields.map(f => {
                        const configKey = QUOTA_CONFIG_MAP[f.key];
                        const isEnabled = !allDisabled && (configKey ? quotaConfig[configKey] !== false : true);
                        const isDisabled = allDisabled || !isEnabled;
                        return (
                          <div key={f.key} className={p.quotaField} style={{ ...(isDisabled ? { opacity: 0.35 } : {}), position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                              <label className={p.quotaLabel}>{f.l}</label>
                              {!allDisabled && configKey && (
                                <div
                                  onClick={(e) => { e.stopPropagation(); handleToggleQuota(f.key); }}
                                  style={{ width: 28, height: 16, borderRadius: 8, background: isEnabled ? '#73c991' : 'var(--border-color)', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}
                                >
                                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: isEnabled ? 14 : 2, transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                                </div>
                              )}
                            </div>
                            <div className={p.quotaValue}>{formatQuota(selClasico.quota?.[f.key])}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Stat Team Totals per team */}
              {[homeC, awayC].filter(Boolean).map(t => {
                const visibleTT = STAT_TT_FIELDS.filter(f => t[f.lineKey] != null && (t[f.oKey] != null || t[f.uKey] != null));
                if (visibleTT.length === 0) return null;
                return (
                <div key={`stt-${t.id}`} style={{ marginBottom: 12 }}>
                  <h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>{getSideLabel(t)} — Stat Team Totals</h4>
                  <div className={p.quotaGrid}>
                    {visibleTT.map(f => {
                      const lineVal = t[f.lineKey];
                      return (
                        <React.Fragment key={f.lineKey}>
                          <div className={p.quotaField}>
                            <label className={p.quotaLabel}>{f.l} {lineVal}</label>
                            <div className={p.quotaValue}>Line: {formatQuota(lineVal)}</div>
                          </div>
                          {t[f.oKey] != null && Number(t[f.oKey]) >= 1 && (
                            <div className={p.quotaField}>
                              <label className={p.quotaLabel}>Over</label>
                              <div className={p.quotaValue}>{formatQuota(t[f.oKey])}</div>
                            </div>
                          )}
                          {t[f.uKey] != null && Number(t[f.uKey]) >= 1 && (
                            <div className={p.quotaField}>
                              <label className={p.quotaLabel}>Under</label>
                              <div className={p.quotaValue}>{formatQuota(t[f.uKey])}</div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
          {resultModalB && (
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0 14px 14px 0', boxShadow: '0 16px 48px rgba(0,0,0,0.5)', width: 380, maxHeight: '85vh', display: 'flex', flexDirection: 'column', flexShrink: 0, animation: 'slideUp 0.2s ease' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>{resultModalB.label}</h3>
                <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem' }} onClick={() => setResultModalB(null)}>✕</button>
              </div>
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                <div className={p.formGroup}><label className={p.formLabel}>Match Status</label><select className={p.formSelect} value={resultModalB.resultStatus} onChange={e => updateResultSlot('B', 'resultStatus', e.target.value)}><option value="">— none —</option>{STATUSES.map(s => <option key={s.id} value={s.code}>{s.code}</option>)}</select></div>
                {resultModalB.resultTeams.map((t, i) => (
                  <div key={i} className={p.resultSection} style={{ marginBottom: 10, background: i === 0 ? 'rgba(108,140,191,0.06)' : 'rgba(212,160,82,0.06)', borderColor: i === 0 ? 'rgba(108,140,191,0.15)' : 'rgba(212,160,82,0.15)' }}>
                    <div className={p.resultLabel}>{t.name}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className={p.fieldGroup}><label className={p.fieldLabel}>Time 1st Goal <span style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>(mm:ss.ms)</span></label><input className={p.resultInput} placeholder="e.g. 12:34.567" value={t.timeOfFirstGoal ?? ''} onChange={e => updateResultSlotTeam('B', i, 'timeOfFirstGoal', e.target.value)} /></div>
                      <div className={p.fieldGroup}><label className={p.fieldLabel}>FH Goals</label><input className={p.resultInput} value={t.firstHalfGoals ?? ''} onChange={e => updateResultSlotTeam('B', i, 'firstHalfGoals', e.target.value)} /></div>
                      <div className={p.fieldGroup}><label className={p.fieldLabel}>FT Goals</label><input className={p.resultInput} value={t.fullTimeGoals ?? ''} onChange={e => updateResultSlotTeam('B', i, 'fullTimeGoals', e.target.value)} /></div>
                      {EXTRA_RESULT_FIELDS.map(f => (
                        <div key={f.key} className={p.fieldGroup}><label className={p.fieldLabel}>{f.label}</label><input className={p.resultInput} value={t[f.key] ?? ''} onChange={e => updateResultSlotTeam('B', i, f.key, e.target.value)} /></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end' }}>
                <button className={p.btnPrimary} onClick={() => handleResultUpdate('B')}>{resultModalB.resultsExist ? 'Update' : 'Create'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Modal — Lambdas & Margins (over everything) */}
      {settingsOpen && selClasico && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setSettingsOpen(false)}>
          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 14, boxShadow: '0 16px 48px rgba(0,0,0,0.5)', width: 560, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Clasico Settings — #{selClasico.id}</h3>
              <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem' }} onClick={() => setSettingsOpen(false)}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>

              {/* Lambda Section */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Lambda Values</h4>
                  <button className={p.btnPrimary} style={{ fontSize: '0.75rem', padding: '5px 14px' }} onClick={handleSaveLambdas}>Update Lambdas</button>
                </div>
                {[
                  { label: 'Goals', fields: [
                    { key: 'lambdaHome', l: 'λ Home' }, { key: 'lambdaAway', l: 'λ Away' }, { key: 'lambdaTotal', l: 'λ Total' },
                  ]},
                  { label: 'Goals HT/2H (inherited)', readonly: true, fields: [
                    { key: 'lambdaHomeHt', l: 'λ Home HT' }, { key: 'lambdaAwayHt', l: 'λ Away HT' },
                    { key: 'lambdaHomeSh', l: 'λ Home 2H' }, { key: 'lambdaAwaySh', l: 'λ Away 2H' },
                  ]},
                  { label: 'Corners', readonly: true, fields: [
                    { key: 'lambdaHomeCorners', l: 'λ Home Corners' }, { key: 'lambdaAwayCorners', l: 'λ Away Corners' },
                    { key: 'lambdaHomeHtCorners', l: 'λ Home HT Corners' }, { key: 'lambdaAwayHtCorners', l: 'λ Away HT Corners' },
                  ]},
                  { label: 'Yellow Cards', readonly: true, fields: [
                    { key: 'lambdaHomeYc', l: 'λ Home YC' }, { key: 'lambdaAwayYc', l: 'λ Away YC' },
                    { key: 'lambdaHomeHtYc', l: 'λ Home HT YC' }, { key: 'lambdaAwayHtYc', l: 'λ Away HT YC' },
                  ]},
                  { label: 'Shots on Target', readonly: true, fields: [
                    { key: 'lambdaHomeSot', l: 'λ Home SOT' }, { key: 'lambdaAwaySot', l: 'λ Away SOT' },
                    { key: 'lambdaHomeHtSot', l: 'λ Home HT SOT' }, { key: 'lambdaAwayHtSot', l: 'λ Away HT SOT' },
                  ]},
                ].map(sec => (
                  <div key={sec.label} style={{ marginBottom: 12 }}>
                    <h5 style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>{sec.label}</h5>
                    <div className={p.quotaGrid}>
                      {sec.fields.map(f => (
                        <div key={f.key} className={p.quotaField} style={sec.readonly ? { opacity: 0.6 } : {}}>
                          <label className={p.quotaLabel}>{f.l}{sec.readonly ? <span style={{ fontSize: '0.6rem' }}> (calc)</span> : ''}</label>
                          <input className={p.quotaInput} style={sec.readonly ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : {}} value={lambdas[f.key] ?? ''} readOnly={!!sec.readonly} disabled={!!sec.readonly} onChange={sec.readonly ? undefined : e => setLambdas(prev => ({ ...prev, [f.key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ height: 1, background: 'var(--border-color)', margin: '0 0 24px' }} />

              {/* Margins Section */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Margin Overrides (%)</h4>
                  <button className={p.btnPrimary} style={{ fontSize: '0.75rem', padding: '5px 14px' }} onClick={handleSaveMargins}>Update Margins</button>
                </div>
                {[
                  { label: 'FT Markets', fields: [
                    { key: 'margin1x2', l: '1x2' }, { key: 'marginDoubleChance', l: 'DC' }, { key: 'marginFirstGoal', l: 'First Goal' },
                  ]},
                  { label: 'FT Over/Under', fields: [
                    { key: 'marginOu25', l: 'O/U 2.5' }, { key: 'marginOu35', l: 'O/U 3.5' }, { key: 'marginOu45', l: 'O/U 4.5' },
                    { key: 'marginOu55', l: 'O/U 5.5' }, { key: 'marginOu65', l: 'O/U 6.5' },
                  ]},
                  { label: 'FT Handicap', fields: [
                    { key: 'marginEh10', l: 'EH 1:0' }, { key: 'marginEh01', l: 'EH 0:1' },
                  ]},
                  { label: 'HT Markets', fields: [
                    { key: 'marginHt1x2', l: 'HT 1x2' }, { key: 'marginHtDoubleChance', l: 'HT DC' }, { key: 'marginHtBtts', l: 'HT BTTS' },
                  ]},
                  { label: 'HT Over/Under', fields: [
                    { key: 'marginHtOu05', l: 'O/U 0.5' }, { key: 'marginHtOu15', l: 'O/U 1.5' }, { key: 'marginHtOu25', l: 'O/U 2.5' },
                  ]},
                  { label: 'HT Handicap', fields: [
                    { key: 'marginHtEh10', l: 'EH 1:0' }, { key: 'marginHtEh01', l: 'EH 0:1' },
                  ]},
                  { label: '2nd Half', fields: [
                    { key: 'marginSh1x2', l: '1x2' }, { key: 'marginShDoubleChance', l: 'DC' }, { key: 'marginShBtts', l: 'BTTS' },
                    { key: 'marginShOu05', l: 'O/U 0.5' }, { key: 'marginShOu15', l: 'O/U 1.5' }, { key: 'marginShOu25', l: 'O/U 2.5' },
                    { key: 'marginShEh10', l: 'EH 1:0' }, { key: 'marginShEh01', l: 'EH 0:1' },
                  ]},
                  { label: 'Special', fields: [
                    { key: 'marginHsh', l: 'HSH' }, { key: 'marginWtn', l: 'WTN' }, { key: 'marginDnb', l: 'DNB' },
                  ]},
                  { label: 'Corners FT', fields: [
                    { key: 'marginCornersFt1x2', l: '1x2' }, { key: 'marginCornersFtDoubleChance', l: 'DC' },
                    { key: 'marginCornersFtOu1', l: 'O/U 1' }, { key: 'marginCornersFtOu2', l: 'O/U 2' }, { key: 'marginCornersFtOu3', l: 'O/U 3' },
                    { key: 'marginCornersFtEh10', l: 'EH 1:0' }, { key: 'marginCornersFtEh01', l: 'EH 0:1' }, { key: 'marginCornersFtEt', l: 'ET' },
                  ]},
                  { label: 'Corners 1H', fields: [
                    { key: 'marginCornersHt1x2', l: '1x2' }, { key: 'marginCornersHtDoubleChance', l: 'DC' },
                    { key: 'marginCornersHtOu1', l: 'O/U 1' }, { key: 'marginCornersHtOu2', l: 'O/U 2' }, { key: 'marginCornersHtOu3', l: 'O/U 3' },
                    { key: 'marginCornersHtEh10', l: 'EH 1:0' }, { key: 'marginCornersHtEh01', l: 'EH 0:1' }, { key: 'marginCornersHtBt', l: 'BT' },
                  ]},
                  { label: 'Yellow Cards FT', fields: [
                    { key: 'marginYcFt1x2', l: '1x2' }, { key: 'marginYcFtDoubleChance', l: 'DC' },
                    { key: 'marginYcFtOu1', l: 'O/U 1' }, { key: 'marginYcFtOu2', l: 'O/U 2' }, { key: 'marginYcFtOu3', l: 'O/U 3' },
                    { key: 'marginYcFtEh10', l: 'EH 1:0' }, { key: 'marginYcFtEh01', l: 'EH 0:1' }, { key: 'marginYcFtEt', l: 'ET' },
                  ]},
                  { label: 'Yellow Cards 1H', fields: [
                    { key: 'marginYcHt1x2', l: '1x2' }, { key: 'marginYcHtDoubleChance', l: 'DC' },
                    { key: 'marginYcHtOu1', l: 'O/U 1' }, { key: 'marginYcHtOu2', l: 'O/U 2' }, { key: 'marginYcHtOu3', l: 'O/U 3' },
                    { key: 'marginYcHtEh10', l: 'EH 1:0' }, { key: 'marginYcHtEh01', l: 'EH 0:1' }, { key: 'marginYcHtEt', l: 'ET' },
                  ]},
                  { label: 'SOT FT', fields: [
                    { key: 'marginSotFt1x2', l: '1x2' }, { key: 'marginSotFtDoubleChance', l: 'DC' },
                    { key: 'marginSotFtOu1', l: 'O/U 1' }, { key: 'marginSotFtOu2', l: 'O/U 2' }, { key: 'marginSotFtOu3', l: 'O/U 3' },
                    { key: 'marginSotFtEh10', l: 'EH 1:0' }, { key: 'marginSotFtEh01', l: 'EH 0:1' }, { key: 'marginSotFtEt', l: 'ET' },
                  ]},
                  { label: 'SOT 1H', fields: [
                    { key: 'marginSotHt1x2', l: '1x2' }, { key: 'marginSotHtDoubleChance', l: 'DC' },
                    { key: 'marginSotHtOu1', l: 'O/U 1' }, { key: 'marginSotHtOu2', l: 'O/U 2' }, { key: 'marginSotHtOu3', l: 'O/U 3' },
                    { key: 'marginSotHtEh10', l: 'EH 1:0' }, { key: 'marginSotHtEh01', l: 'EH 0:1' }, { key: 'marginSotHtBt', l: 'BT' },
                  ]},
                ].map(sec => (
                  <div key={sec.label} style={{ marginBottom: 16 }}>
                    <h5 style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>{sec.label}</h5>
                    <div className={p.quotaGrid}>
                      {sec.fields.map(f => (
                        <div key={f.key} className={p.quotaField}>
                          <label className={p.quotaLabel}>{f.l}</label>
                          <input className={p.quotaInput} value={margins[f.key] ?? ''} onChange={e => setMargins(prev => ({ ...prev, [f.key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <WinningMarketsModal
        open={winModalOpen}
        onClose={() => setWinModalOpen(false)}
        title={selClasico ? `Winning Markets — Clasico #${selClasico.id}` : 'Winning Markets'}
        fictiveMatchId={selClasico?.id}
      />
    </div>
  );
}

